/**
 * @license
 * Copyright 2019 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @file Tab for showing layer data sources and coordinate transforms.
 */

import { LocalDataSource } from 'neuroglancer/datasource';
import { changeLayerName, changeLayerType, NewUserLayer, UserLayer, UserLayerConstructor } from 'neuroglancer/layer';
import { LayerDataSource, LoadedDataSubsource, LoadedLayerDataSource } from 'neuroglancer/layer_data_source';
import { MeshSource, MultiscaleMeshSource } from 'neuroglancer/mesh/frontend';
import { SkeletonSource } from 'neuroglancer/skeleton/frontend';
import { MultiscaleVolumeChunkSource } from 'neuroglancer/sliceview/volume/frontend';
import { TrackableBooleanCheckbox } from 'neuroglancer/trackable_boolean';
import { WatchableValue, WatchableValueInterface } from 'neuroglancer/trackable_value';
import { animationFrameDebounce, DebouncedFunction } from 'neuroglancer/util/animation_frame_debounce';
import { CancellationToken } from 'neuroglancer/util/cancellation';
import { DataType } from 'neuroglancer/util/data_type';
import { Borrowed, RefCounted } from 'neuroglancer/util/disposable';
import { removeChildren, removeFromParent, updateChildren } from 'neuroglancer/util/dom';
import { MessageList, MessageSeverity } from 'neuroglancer/util/message_list';
import { makeAddButton } from 'neuroglancer/widget/add_button';
import { CoordinateSpaceTransformWidget } from 'neuroglancer/widget/coordinate_transform';
import { AutocompleteTextInput, makeCompletionElementWithDescription } from 'neuroglancer/widget/multiline_autocomplete';
import { Tab } from 'neuroglancer/widget/tab_view';
import { viewer } from 'src/main';
import './layer_data_sources_tab.css';
import { makeElement } from './layer_gene_table_tab';


export class SourceUrlAutocomplete extends AutocompleteTextInput {
  dataSourceView: DataSourceView;
  dirty: WatchableValueInterface<boolean>;
  constructor(dataSourceView: DataSourceView) {
    const {manager} = dataSourceView.source.layer;
    const sourceCompleter = (value: string, cancellationToken: CancellationToken) =>
        manager.dataSourceProviderRegistry
            .completeUrl({url: value, chunkManager: manager.chunkManager, cancellationToken})
            .then(originalResult => ({
                    completions: originalResult.completions,
                    makeElement: makeCompletionElementWithDescription,
                    offset: originalResult.offset,
                    showSingleResult: true,
                  }));
    super({completer: sourceCompleter, delay: 0});
    this.placeholder = 'Data source URL';
    this.dataSourceView = dataSourceView;
    this.element.classList.add('neuroglancer-layer-data-source-url-input');
    this.element.style.display = 'none';
    this.dirty = new WatchableValue(false);
    const updateDirty = (value: string) => {
      if (value !== this.dataSourceView.source.spec.url) {
        this.dirty.value = true;
      }
    };
    updateDirty('');
    this.onInput.add(updateDirty);
  }

  cancel() {
    this.value = this.dataSourceView.source.spec.url;
    this.dirty.value = false;
    this.inputElement.blur();
    return true;
  }
}

export class MessagesView extends RefCounted {
  element = document.createElement('ul');
  generation = -1;

  constructor(public model: MessageList) {
    super();
    this.element.classList.add('neuroglancer-layer-data-sources-source-messages');
    const debouncedUpdateView =
        this.registerCancellable(animationFrameDebounce(() => this.updateView()));
    this.registerDisposer(model.changed.add(debouncedUpdateView));
    this.registerDisposer(() => removeFromParent(this.element));
    this.updateView();
  }

  updateView() {
    const {model} = this;
    const generation = model.changed.count;
    if (generation === this.generation) return;
    this.generation = generation;
    const {element} = this;
    removeChildren(element);
    const seen = new Set<string>();
    for (const message of model) {
      const key = `${message.severity} ${message.message}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const li = document.createElement('li');
      element.appendChild(li);
      li.classList.add('neuroglancer-message');
      li.classList.add(`neuroglancer-message-${MessageSeverity[message.severity]}`);
      li.textContent = message.message;
    }
  }
}

export class DataSourceSubsourceView extends RefCounted {
  element = document.createElement('div');

  constructor(loadedSource: LoadedLayerDataSource, public loadedSubsource: LoadedDataSubsource) {
    super();
    const {element} = this;
    element.classList.add('neuroglancer-layer-data-source-subsource');
    // element.style.display = 'none';
    const sourceInfoLine = document.createElement('label');
    const sourceType = document.createElement('span');
    const updateActiveAttribute = () => {
      sourceInfoLine.dataset.isActive =
          (loadedSubsource.activated !== undefined || !loadedSubsource.enabled).toString();
    };
    updateActiveAttribute();
    this.registerDisposer(loadedSubsource.isActiveChanged.add(updateActiveAttribute));
    this.registerDisposer(loadedSource.enabledSubsourcesChanged.add(updateActiveAttribute));
    const enabledCheckbox = this.registerDisposer(new TrackableBooleanCheckbox({
      get value() {
        return loadedSubsource.enabled;
      },
      set value(value: boolean) {
        loadedSubsource.enabled =  value;
        loadedSource.enableDefaultSubsources = false;
        loadedSource.enabledSubsourcesChanged.dispatch();
      },
      changed: loadedSource.enabledSubsourcesChanged,
    }));
    sourceInfoLine.classList.add('neuroglancer-layer-data-sources-info-line');
    sourceInfoLine.appendChild(enabledCheckbox.element);

    const sourceId = document.createElement('span');
    sourceId.classList.add('neuroglancer-layer-data-sources-source-id');
    const {id} = loadedSubsource.subsourceEntry;
    if (id !== 'default') {
      sourceId.textContent = id;
    }
    sourceInfoLine.appendChild(sourceId);
    sourceType.classList.add('neuroglancer-layer-data-sources-source-type');
    const messagesView = this.registerDisposer(new MessagesView(this.loadedSubsource.messages));
    element.appendChild(sourceInfoLine);
    sourceInfoLine.appendChild(sourceType);
    element.appendChild(messagesView.element);
    let sourceTypeStr = '';
    const {subsource} = loadedSubsource.subsourceEntry;
    const {volume} = subsource;
    if (volume instanceof MultiscaleVolumeChunkSource) {
      sourceTypeStr = `${DataType[volume.dataType].toLowerCase()} volume`;
    } else if (subsource.mesh instanceof MeshSource) {
      sourceTypeStr = 'meshes (single-res.)';
    } else if (subsource.mesh instanceof MultiscaleMeshSource) {
      sourceTypeStr = 'meshes (multi-res.)';
    } else if (subsource.mesh instanceof SkeletonSource) {
      sourceTypeStr = 'skeletons';
    } else if (subsource.segmentPropertyMap !== undefined) {
      sourceTypeStr = 'segment property map';
    } else if (subsource.local !== undefined) {
      switch (subsource.local) {
        case LocalDataSource.annotations:
          sourceTypeStr = 'Local annotations';
          break;
        case LocalDataSource.equivalences:
          sourceTypeStr = 'local segmentation graph';
          break;
      }
    } else if (subsource.staticAnnotations !== undefined) {
      sourceTypeStr = 'default annotations';
    } else if (subsource.annotation !== undefined) {
      sourceTypeStr = 'annotations';
    } else if (subsource.singleMesh !== undefined) {
      sourceTypeStr = 'single mesh';
    } else if (subsource.segmentationGraph !== undefined) {
      sourceTypeStr = 'segmentation graph';
    }
    sourceType.textContent = sourceTypeStr;
  }
}

export class LoadedDataSourceView extends RefCounted {
  element = document.createElement('div');

  constructor(public source: Borrowed<LoadedLayerDataSource>) {
    super();
    const {element} = this;
    const enableDefaultSubsourcesLabel = document.createElement('label');
    enableDefaultSubsourcesLabel.classList.add('neuroglancer-layer-data-sources-source-default');
    // enableDefaultSubsourcesLabel.style.display = 'none';
    enableDefaultSubsourcesLabel.appendChild(this.registerDisposer(new TrackableBooleanCheckbox({
                                                   changed: source.enabledSubsourcesChanged,
                                                   get value() {
                                                     return source.enableDefaultSubsources;
                                                   },
                                                   set value(value: boolean) {
                                                     if (source.enableDefaultSubsources === value)
                                                       return;
                                                     source.enableDefaultSubsources = value;
                                                     if (value) {
                                                       for (const subsource of source.subsources) {
                                                         subsource.enabled =
                                                             subsource.subsourceEntry.default;
                                                       }
                                                     }
                                                     source.enabledSubsourcesChanged.dispatch();
                                                   },
                                                 }))
                                                 .element);
    enableDefaultSubsourcesLabel.appendChild(
        document.createTextNode('Enable default subsource set'));
    enableDefaultSubsourcesLabel.title =
        'Enable the default set of subsources for this data source.';
    element.appendChild(enableDefaultSubsourcesLabel);
    for (const subsource of source.subsources) {
      if(subsource.subsourceEntry.id === 'bounds'){
        subsource.enabled = false
      }
      element.appendChild(
          this.registerDisposer(new DataSourceSubsourceView(source, subsource)).element);
    }
    const {transform} = source;
    if (transform.mutableSourceRank || transform.value.sourceRank !== 0) {
      const transformWidget = this.registerDisposer(new CoordinateSpaceTransformWidget(
          source.transform, source.layer.localCoordinateSpaceCombiner,
          source.layer.manager.root.coordinateSpaceCombiner));
      this.element.appendChild(transformWidget.element);
    }
    this.registerDisposer(() => removeFromParent(this.element));
  }
}

export class showCellBinView extends RefCounted {
  cellbinContainer = makeElement('div', ['changeCellbinContainer']);
  checkbox = makeElement("input") as HTMLInputElement;
  constructor( public source: Borrowed<LoadedLayerDataSource>, type: string, isImg:any, isCellbinDnb?: boolean ) {
    super();
    const { cellbinContainer, checkbox } = this;
    this.registerDisposer(() => removeFromParent(cellbinContainer));
    checkbox.type = 'checkbox';
    let cellbin = makeElement('div', ['showCellbin']);
    let labelText = '';
    let json = viewer.state.toJSON();
    let layers = json.layers;
    let curcellbindnb = isCellbinDnb;   //  读取是否为普通bin + cellbin的形式
    let _cururl:any = source.layerDataSource.spec_.url.split('/');
    for(let i = 0; i < layers.length; i++) {
      if(curcellbindnb){
        if(type === 'annotation'){
          labelText = 'Click to show cellbin';
        }else{
          labelText = 'Click to show dnb';
        }
      }else if( layers[i].type === type ) {
        if(type === 'annotation'){
          labelText = 'Click to show/hide cellbin';
        }else{
          labelText = 'Click to show/hide dnb';
        }
      }else if(layers[i].type === 'image'){}else{
      }
    }
    // this.findIsChecked(type, isCellbinDnb ? true : false);
    let label = makeElement('label', ['cellbinLabel'], {'style': 'color: #333'}, labelText);
    type === 'annotation' ? (_cururl.splice(_cururl.length - 2, 2) ): (_cururl.splice(_cururl.length - 1, 1) );
    checkbox.addEventListener('change', (e: Event)=>{
      let _layers:any = [];
      if(curcellbindnb){
        if(type === 'annotation'){
          json.geneTableUrl = _cururl.join('/').replace('annotation','cellbin').replace('precomputed://','');
          if(isImg.length > 0){
            _layers.push({
              "type": 'image',
              "source" : _cururl.join('/').replace('annotation', 'image') + "/ssDNA",
              "tab": "source",
              "shader": `#uicontrol invlerp normalized
              void main() {
                emitGrayscale(normalized());
              }`,
              "name": "image",
              "visible": true,
              "cellbindnb": "cellbindnb",
              "genetype": "CDimg",
              "blend": "additive"
            });
          };
          _layers.push({
            "type": "segmentation",
            "source": 
              [_cururl.join('/').replace('annotation','segmentation') + "/cellbin",
              _cururl.join('/').replace('annotation','segmentation') + "/cellbin/labels",
            ],
            "tab": "segments",
            "name": "segmentation",
            "visible": true,
            "cellbindnb": "cellbindnb",
            "genetype": "CDseg"
          },{
            "type": 'annotation',
            "source" : _cururl.join('/') + "/cellbin/bin1",
            "tab": "source",
            "genename": "annotationCellbin",
            "shader": "\nvoid main() {\n  setColor(defaultColor());\n  setPointMarkerFactor(1.5*sqrt(float(prop_area())/3.14159));\n  setPointMarkerOpacityFactor(0.);\n}\n",
            "name": "annotation",
            "visible": true,
            "cellbindnb": "cellbindnb",
            "genetype": "CDanno"
          });          
        }else{
          if(isImg.length > 0){
            _layers.push({
              "type": 'image',
              "source" : _cururl.join('/').replace('segmentation', 'image') + "/ssDNA",
              "tab": "source",
              "shader": `#uicontrol invlerp normalized
              void main() {
                emitGrayscale(normalized());
              }`,
              "name": "image",
              "visible": true,
              "cellbindnb": "cellbindnb",
              "genetype": "CDimg",
              "blend": "additive"
            });
          };
          _layers.push({
            "type": 'annotation',
            "source" : _cururl.join('/').replace('segmentation', 'annotation') + "/dnb/bin100",
            "tab": "source",
            // "shader": "\nvoid main() {\n  setColor(defaultColor());\n  setPointMarkerFactor(1.5*sqrt(float(prop_area())/3.14159));\n  setPointMarkerOpacityFactor(0.);\n}\n",
            "name": "annotation",
            "visible": true,
            "cellbindnb": "cellbindnb",
            "genetype": "CDanno",
            "shader": `#uicontrol float sizeFactor slider(min=0.0, max=4.0, default=1.0, step=0.01); 
           #uicontrol float oFactor slider(min=0.0, max=1.0, default=1.0, step=0.01);
           void main() { 
            setColor(colormapNonlinear(prop_color(),vec3[9](vec3 (0.05,0.20,0.51),vec3 (0.00,0.37,0.64),vec3 (0.04,0.53,0.73),vec3 (0.00,0.76,0.60),vec3 (0.95,0.83,0.22),vec3 (0.96,0.69,0.20),vec3 (0.95,0.56,0.22),vec3 (0.91,0.38,0.14),vec3 (0.85,0.12,0.12))));
             setPointMarkerFactor(sizeFactor);
             setPointMarkerOpacityFactor(oFactor - 0.01);
           }`
          });
        }
        json.layers = _layers;
        json.genePanel = {
          "visible": false
        }
        viewer.state.restoreState(json);
        setTimeout(() => {
          let newJson = viewer.state.toJSON();
          newJson.genePanel = {
            "visible": true
          }
          viewer.state.restoreState(newJson);
        }, 0);
      }else{
        ( e.target as HTMLInputElement ).checked ? this.checkbox.setAttribute('checked', 'true') : this.checkbox.removeAttribute('checked');
        layers.forEach((item:any)=>{
          if(item.type === type){
            item.visible = checkbox.getAttribute('checked') !== 'true';
          }else{
            item.visible = checkbox.getAttribute('checked') === 'true';
          }
        })
        viewer.state.restoreState(json);
      }
    });
    cellbin.appendChild(checkbox);
    cellbin.appendChild(label);
    cellbinContainer.appendChild(cellbin);
  }
  // findIsChecked(type: string, isCellbinDnb: boolean){
  //   if(!isCellbinDnb){
  //     let json = viewer.state.toJSON();
  //     let layers = json.layers;
  //     for(let i = 0; i < layers.length; i++) {
  //       if( layers[i].type !== type ) {
  //         (layers[i].visible === true || layers[i].visible === undefined || layers[i].visible === '' ) ? this.checkbox.setAttribute('checked', 'true') : this.checkbox.removeAttribute('checked');
  //       }
  //     }
  //   }
  // }
}

export class SelectBinView extends RefCounted{
  selectBinContainer = makeElement('div', ['selectBinContainer']);
  selectElement = document.createElement('select');
  url = viewer.state;
  inputValue:any;
  lastParam:any;
  constructor(public source: Borrowed<LoadedLayerDataSource>, url: string){
    super();
    const { selectBinContainer, selectElement } = this;
    let label = makeElement('label', ['selectBinLabel'], {}, `Please select binsize：`);
    // 新增url参数，用此来接收修改url时，对应的当前修改的url值
    this.inputValue = url.split('/');
    this.lastParam = this.inputValue.splice(this.inputValue.length - 1, 1);
    selectElement.classList.add('neuroglancer-layer-side-panel-type-hq');
    selectElement.id = 'hqDefineSelect';
    let binList = viewer.dataSource.subsources[0].subsource.annotation?.metadata?.binlist;
    // viewer.dataSource = source !== undefined ? source.dataSource : viewer.dataSource;
    this.registerDisposer(() => removeFromParent(selectBinContainer));
    let json = viewer.state.toJSON().layers;
    let isCellBin = json.filter( (item:any) => { 
      if( typeof item.source === 'object' ){
        if(JSON.stringify(item.source).includes('cellbin')){
          return item 
        } 
      }else if(typeof item.source === 'string' && item.source.includes('cellbin')){
        return item 
      }
    } );
    if(binList?.length>0 && binList.includes(this.lastParam[0])){
      for(let i = 0; i < binList.length; i++){
        selectElement.options.add(new Option(binList[i], binList[i],false, binList[i] === this.lastParam[0]?true:false) );
      }
      isCellBin.length ? selectElement.options.add(new Option('cellbin', 'cellbin',false, false) ) : '';
    }else{
      selectElement.style.display = 'none';
      label.style.display = 'none';
    }
    selectBinContainer.appendChild(label);
    selectBinContainer.appendChild(selectElement);
  }

  onchange = (url:string) => {
    let objS = this.selectElement.value;
    url.substring(0,1);
    if( objS === 'cellbin' ) {
      let newurl = url.split('/');
      newurl.splice(newurl.length-1, 2);
      this.lastParam = newurl.join('/')+'/cellbin/bin1';
      let json = viewer.state.toJSON();
      for(let i = 0; i < json.layers.length; i++){
        if (json.layers[i].url.includes('cellbin')) {
          json.layers[i].shader =  "\n#uicontrol float sizeFactor slider(min=0.0, max=4.0, default=1.0, step=0.01); \n#uicontrol float oFactor slider(min=0.0, max=1.0, default=1.0, step=0.01);\nvoid main() {\n  setColor(colormapNonlinear(prop_color(),vec3[9](vec3 (0.05,0.20,0.51),vec3 (0.00,0.37,0.64),vec3 (0.04,0.53,0.73),vec3 (0.00,0.76,0.60),vec3 (0.95,0.83,0.22),vec3 (0.96,0.69,0.20),vec3 (0.95,0.56,0.22),vec3 (0.91,0.38,0.14),vec3 (0.85,0.12,0.12))));\n  setPointMarkerFactor(sizeFactor*sqrt(float(prop_area())/3.14159));\n  setPointMarkerOpacityFactor(oFactor);\n}\n";
        }
      }
      viewer.state.restoreState(json);
    }else {
      let newurl = url.split('/');
      newurl.splice(newurl.length-1, 1);
      this.lastParam = newurl.join('/')+'/'+objS;
      // urlInput.setValueAndSelection(this.lastParam);
      // urlInput.disableCompletion();
      // urlInput.hideCompletions();
      // urlInput.onCommit.dispatch(this.lastParam, true);
      let json = viewer.state.toJSON();
      for(let i = 0; i < json.layers.length; i++){
        if (json.layers[i].name === json.selectedLayer.layer) {
          json.layers[i].source = this.lastParam
       }
      }
      viewer.state.restoreState(json);
    }
  }
  
}

export class DataSourceView extends RefCounted {
  element = document.createElement('div');
  urlInput: SourceUrlAutocomplete;

  seenGeneration = 0;
  generation = -1;
  private loadedView: LoadedDataSourceView|undefined;
  private selectBinView: SelectBinView|undefined;
  private cellBinView: showCellBinView | undefined;
  constructor(public tab: Borrowed<LayerDataSourcesTab>, public source: Borrowed<LayerDataSource>) {
    super();
    const urlInput = this.urlInput = this.registerDisposer(new SourceUrlAutocomplete(this));
    const updateUrlFromView = (url: string, explicit: boolean) => {
      const {source} = this;
      const existingSpec = source.spec;
      const userLayer = this.source.layer;
      url = userLayer.manager.dataSourceProviderRegistry.normalizeUrl({url});
      if (url !== urlInput.value) {
        urlInput.disableCompletion();
        urlInput.setValueAndSelection(url, {begin: url.length, end: url.length});
      }
      urlInput.dirty.value = false;
      // If url is non-empty and unchanged, don't set spec, as that would trigger a reload of the
      // data source.  If the url is empty, always set spec in order to possible remove the empty
      // data source.
      if (url && url === existingSpec.url) {
        if (explicit) {
          if (tab.detectedLayerConstructor !== undefined) {
            changeLayerTypeToDetected(source.layer);
          }
        }
        return;
      }
      if (userLayer instanceof NewUserLayer) {
        try {
          const newName = userLayer.manager.dataSourceProviderRegistry.suggestLayerName(url);
          changeLayerName(userLayer.managedLayer, newName);
        } catch {
        }
      }
      source.spec = {...existingSpec, url};
    };
    urlInput.onCommit.add(updateUrlFromView);

    const {element} = this;
    element.classList.add('neuroglancer-layer-data-source');
    element.appendChild(urlInput.element);
    element.appendChild(this.registerDisposer(new MessagesView(source.messages)).element);
    this.updateView();
  }

  updateView() {
    const generation = this.source.changed.count;
    if (generation === this.generation) return;
    this.generation = generation;
    this.urlInput.value = this.source.spec.url;
    this.urlInput.dirty.value = false;
    const {loadState} = this.source;
    let {loadedView, selectBinView, cellBinView} = this;
    if (loadedView !== undefined) {
      if (loadedView.source === loadState) {
        return;
      }
      loadedView.dispose();
      loadedView = this.loadedView = undefined;
    }
    if (selectBinView !== undefined) {
      selectBinView.dispose();
      selectBinView = this.selectBinView = undefined;
    }
    if( cellBinView !== undefined ) {
      cellBinView.dispose();
      cellBinView = this.cellBinView = undefined;
    }
    if (loadState instanceof LoadedLayerDataSource) {
      loadedView = this.loadedView = new LoadedDataSourceView(loadState);
      let cururl = loadedView.source.layerDataSource.spec_.url;    //  获取当前source面板的url
      if(!cururl.includes('labels')){
        selectBinView = this.selectBinView = new SelectBinView(loadState, cururl);
        this.element.appendChild(selectBinView.selectBinContainer)
        selectBinView.selectElement.onchange =() =>{
          selectBinView?.onchange(this.urlInput.inputElement.innerText);
        }
      }
      let _layers = viewer.state.toJSON().layers;
      let isImg = _layers.filter((item:any)=> {return item.type === 'image'});
      if(_layers[0].cellbindnb === 'cellbindnb' && !cururl.includes('label') && !cururl.includes('image')){
        cellBinView = this.cellBinView = new showCellBinView( loadState, loadState.layerDataSource.layer.type, isImg, true);
        this.element.appendChild( cellBinView.cellbinContainer );
      }
      this.element.appendChild(loadedView.element);
    }
  }

  disposed() {
    const {loadedView} = this;
    if (loadedView !== undefined) {
      loadedView.dispose();
    }
    removeFromParent(this.element);
    super.disposed();
  }
}

function changeLayerTypeToDetected(userLayer: UserLayer) {
  if (userLayer instanceof NewUserLayer) {
    const layerConstructor = userLayer.detectedLayerConstructor;
    if (layerConstructor !== undefined) {
      changeLayerType(userLayer.managedLayer, layerConstructor);
      return true;
    }
  }
  return false;
}

export class LayerDataSourcesTab extends Tab {
  generation = -1;
  sourceViews = new Map<LayerDataSource, DataSourceView>();
  private addDataSourceIcon = makeAddButton({title: 'Add additional data source'});
  private layerTypeDetection = document.createElement('div');
  private layerTypeElement = document.createElement('span');
  private dataSourcesContainer = document.createElement('div');
  private reRender: DebouncedFunction;
  
  constructor(public layer: Borrowed<UserLayer>) {
    super();
    const {element, dataSourcesContainer} = this;
    element.classList.add('neuroglancer-layer-data-sources-tab');
    dataSourcesContainer.classList.add('neuroglancer-layer-data-sources-container');
    const {addDataSourceIcon} = this;
    addDataSourceIcon.style.alignSelf = 'start';
    addDataSourceIcon.style.display = 'none';
    addDataSourceIcon.addEventListener('click', () => {
      const layerDataSource = this.layer.addDataSource(undefined);
      this.updateView();
      const view = this.sourceViews.get(layerDataSource);
      if (view === undefined) return;
      view.urlInput.inputElement.focus();
    });
    element.appendChild(this.dataSourcesContainer);
    if (layer instanceof NewUserLayer) {
      const {layerTypeDetection, layerTypeElement} = this;
      layerTypeDetection.style.display = 'none';
      layerTypeElement.classList.add('neuroglancer-layer-data-sources-tab-type-detection-type');
      layerTypeDetection.appendChild(document.createTextNode(`Create as `));
      layerTypeDetection.appendChild(layerTypeElement);
      layerTypeDetection.appendChild(document.createTextNode(` layer`));
      element.appendChild(layerTypeDetection);
      layerTypeDetection.classList.add('neuroglancer-layer-data-sources-tab-type-detection');
      layerTypeDetection.addEventListener('click', () => {
        changeLayerTypeToDetected(layer);
      });
    }
    const reRender = this.reRender = animationFrameDebounce(() => this.updateView());
    this.registerDisposer(layer.dataSourcesChanged.add(reRender));
    this.registerDisposer(this.visibility.changed.add(reRender));
    this.updateView();
  }

  detectedLayerConstructor: UserLayerConstructor|undefined = undefined;

  updateLayerTypeDetection() {
    const layerConstructor = (() => {
      const userLayer = this.layer;
      if (!(userLayer instanceof NewUserLayer)) return undefined;
      const layerConstructor = userLayer.detectedLayerConstructor;
      if (layerConstructor === undefined) return undefined;
      for (const view of this.sourceViews.values()) {
        if (view.urlInput.dirty.value) return undefined;
      }
      return layerConstructor;
    })();
    if (layerConstructor === this.detectedLayerConstructor) return;
    const {layerTypeDetection} = this;
    this.detectedLayerConstructor = layerConstructor;
    if (layerConstructor !== undefined) {
      const {layerTypeElement} = this;
      layerTypeElement.textContent = layerConstructor.type;
      layerTypeDetection.title =
          `Click here or press enter in the data source URL input box to create as ` +
          `${layerConstructor.type} layer`;
      layerTypeDetection.style.display = '';
    } else {
      layerTypeDetection.style.display = 'none';
    }
  }

  disposed() {
    const {sourceViews} = this;
    for (const dataSource of sourceViews.values()) {
      dataSource.dispose();
    }
    sourceViews.clear();
    super.disposed();
  }

  private updateView() {
    if (!this.visible) return;
    const generation = this.layer.dataSourcesChanged.count;
    if (generation !== this.generation) {
      this.generation = generation;
      const curSeenGeneration = Date.now();
      const {sourceViews} = this;
      const {layer} = this;
      function* getChildNodes(this: LayerDataSourcesTab) {
        let lastSourceUrlEmpty = true;
        const {dataSources} = layer;
        for (const source of dataSources) {
          let view = sourceViews.get(source);
          if (view === undefined) {
            view = new DataSourceView(this, source); // urlInput
            view.registerDisposer(view.urlInput.dirty.changed.add(this.reRender));
            sourceViews.set(source, view);
          }
          view.seenGeneration = curSeenGeneration;
          view.updateView();
          const url = source.spec.url;
          if (dataSources.length === 1 && url === '') {
            setTimeout(() => {
              view!.urlInput.inputElement.focus();
            }, 0);
          }
          lastSourceUrlEmpty = source.spec.url.length === 0;
          yield view.element;
        }
        if (!lastSourceUrlEmpty) {
          yield this.addDataSourceIcon;
        }
      }
      updateChildren(this.dataSourcesContainer, getChildNodes.call(this));
      for (const [source, view] of sourceViews) {
        if (view.seenGeneration !== curSeenGeneration) {
          view.dispose();
          sourceViews.delete(source);
        }
      }
    }
    this.updateLayerTypeDetection();
  }
}
